import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useLocation, useNavigate } from "react-router-dom";

import type { LoginGrantState } from "helpers/login-grant";
import { startRecaptchaPreload } from "helpers/recaptcha";
import { NetworkError } from "hooks/usePublicAPI";
import AuthLayout from "layouts/AuthLayout";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import { confirmedDestination } from "pages/StartLead/awaitingConfirmation";
import {
  leadFlowEvents,
  type ConfirmSurface,
  type SessionEndedStage,
} from "pages/StartLead/events";
import { clearStoredState, loadState, saveState } from "pages/StartLead/state";
import type {
  ConfirmedVia,
  MintedGrant,
} from "pages/StartLead/submissionStatus";
import {
  submissionIdFromSearch,
  type ResumeNext,
} from "pages/StartResume/resume";
import type { TypedDestinations } from "pages/StartVerify/useCorrector";
import { confirmedMethodOf } from "pages/StartVerify/verify";
import { apiErrorCode } from "utils/errors";

import GrantScreenSkeleton from "./GrantScreenSkeleton";
import type { DestinationCorrector } from "./destination-edit";
import { RetryButton, SupportCallLine } from "./GrantParts";
import { LoginGrantConfirm } from "./LoginGrantConfirm";
import { postGrantRequest } from "./login-grant-api";
import {
  lookupFailureOf,
  parseGrantLookup,
  renewalPurposeOf,
  renewalTargetFor,
  renewedHistoryState,
  SUPPORT_LINES,
  supportLineKindFor,
  UNREACHABLE_TITLE,
  unreachableBody,
  type GrantLookup,
} from "./login-grant-flow";
import SessionEnded from "./SessionEnded";
import type { Renewed } from "./session-renewal";

type Phase =
  | { kind: "loading" }
  | { kind: "ended"; lookup: GrantLookup | null; stage: SessionEndedStage }
  | { kind: "unreachable"; rateLimited: boolean; at: number }
  | { kind: "ready"; lookup: GrantLookup };

const LOADING: Phase = { kind: "loading" };

function shownLookup(
  lookup: GrantLookup,
  typed: TypedDestinations | undefined,
): GrantLookup {
  if (typed === undefined) return lookup;
  return {
    ...lookup,
    phoneDisplay:
      lookup.phoneDisplay === null
        ? null
        : (typed.phone ?? lookup.phoneDisplay),
    emailDisplay:
      lookup.emailDisplay === null
        ? null
        : (typed.email ?? lookup.emailDisplay),
  };
}

/**
 * The sign-in screen for a login-grant visit: the destination is the one
 * the submitter typed and the server bound to the grant, so the screen
 * never asks for, or lets anyone change, an identifier. Every request
 * carries the grant and goes without the visitor's own session; a
 * confirmed grant lands on the server-fixed path from the lookup. A grant
 * that has ended is replaced in place; `onUnrenewable` is the host's way to
 * take over when it cannot be.
 */
export default function LoginGrantLogin({
  grant,
  surface,
  onUnrenewable,
  onCorrect,
  typed,
}: {
  grant: LoginGrantState;
  surface: ConfirmSurface;
  onUnrenewable?: () => void;
  /** Saves a correction of the destination, when this screen offers one. */
  onCorrect?: DestinationCorrector;
  /** What the visitor typed in corrections made on this page; shown in place
   * of the destinations the server reveals. */
  typed?: TypedDestinations;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const trackEvent = useTrackLeadFlowEvent();
  return (
    <LoginGrantScreen
      key={grant.loginGrant}
      grant={grant}
      surface={surface}
      searchSubmissionId={submissionIdFromSearch(location.search)}
      onUnrenewable={onUnrenewable}
      onCorrect={onCorrect}
      typed={typed}
      onRenewed={(renewed) => {
        if (renewed.kind === "submission") {
          keepStoredGrant(renewed.minted, renewed.submissionId);
        }
        navigate(location.pathname + location.search, {
          replace: true,
          state: renewedHistoryState(
            location.state,
            renewed.kind === "submission"
              ? renewed.minted.grant
              : renewed.loginGrant,
          ),
        });
      }}
      onConfirmed={(next, submissionId, via) => {
        trackEvent(
          leadFlowEvents.confirmAccepted({
            purpose: "lead_confirmation",
            surface,
            method: confirmedMethodOf(via),
          }),
        );
        clearStoredState();
        navigate(confirmedDestination(next, submissionId), { replace: true });
      }}
    />
  );
}

/** Keeps the replacement grant with the submission stored on this device,
 * so a visit without this history entry resumes on the grant that works. */
function keepStoredGrant(minted: MintedGrant, submissionId: string) {
  const state = loadState();
  const { result } = state;
  if (
    result === null ||
    result.submissionId !== submissionId ||
    result.status !== "awaiting_confirmation"
  ) {
    return;
  }
  saveState({
    ...state,
    result: {
      ...result,
      verification: {
        grant: minted.grant,
        methods: minted.methods,
        phoneDisplay: minted.phoneDisplay,
        emailDisplay: minted.emailDisplay,
      },
    },
  });
}

function LoginGrantScreen({
  grant,
  surface,
  searchSubmissionId,
  onRenewed,
  onConfirmed,
  onUnrenewable,
  onCorrect,
  typed,
}: {
  grant: LoginGrantState;
  surface: ConfirmSurface;
  searchSubmissionId: string | null;
  onRenewed: (renewed: Renewed) => void;
  onConfirmed: (
    next: ResumeNext,
    submissionId: string,
    via: ConfirmedVia,
  ) => void;
  onUnrenewable?: () => void;
  onCorrect?: DestinationCorrector;
  typed?: TypedDestinations;
}) {
  const { loginGrant, leadKind, quoteId } = grant;
  const supportKind = supportLineKindFor(leadKind);
  const [phase, setPhase] = useState<Phase>(LOADING);
  const [attempt, setAttempt] = useState(0);
  const lookedUp = useRef(-1);

  useEffect(startRecaptchaPreload, []);

  useEffect(() => {
    if (lookedUp.current === attempt) return;
    lookedUp.current = attempt;
    postGrantRequest<unknown>("/auth/login-grants/lookup", {
      login_grant: loginGrant,
    })
      .then((raw) => {
        const lookup = parseGrantLookup(raw);
        setPhase(
          lookup
            ? { kind: "ready", lookup }
            : { kind: "ended", lookup: null, stage: "lookup" },
        );
      })
      .catch((err: unknown) => {
        if (!(err instanceof NetworkError)) Sentry.captureException(err);
        const failure =
          err instanceof NetworkError
            ? lookupFailureOf(apiErrorCode(err), err.statusCode)
            : "unreachable";
        setPhase(
          failure === "grant_invalid"
            ? { kind: "ended", lookup: null, stage: "lookup" }
            : {
                kind: "unreachable",
                rateLimited: failure === "rate_limited",
                at: Date.now(),
              },
        );
      });
  }, [loginGrant, attempt]);

  if (phase.kind === "loading") {
    return (
      <GrantScreenSkeleton
        supportKind={supportKind}
        surface={surface}
        withFix={onCorrect !== undefined}
      />
    );
  }

  if (phase.kind === "unreachable") {
    return (
      <AuthLayout
        title={UNREACHABLE_TITLE}
        description={unreachableBody(phase.rateLimited)}
        headerPhone={SUPPORT_LINES[supportKind]}
      >
        <div className="space-y-6">
          <RetryButton
            key={phase.at}
            refusedAt={phase.rateLimited ? phase.at : 0}
            onRetry={() => {
              setPhase(LOADING);
              setAttempt((count) => count + 1);
            }}
          />
          <SupportCallLine
            kind={supportKind}
            surface={surface}
            screen="unreachable"
          />
        </div>
      </AuthLayout>
    );
  }

  if (phase.kind === "ended") {
    const target = renewalTargetFor({
      grant: loginGrant,
      lookup: phase.lookup,
      stored: loadState().result,
      quoteId: quoteId ?? null,
      searchSubmissionId,
    });
    return (
      <SessionEnded
        surface={surface}
        supportKind={supportKind}
        purpose={phase.lookup?.purpose ?? renewalPurposeOf(target)}
        stage={phase.stage}
        target={target}
        onRenewed={onRenewed}
        onConfirmed={onConfirmed}
        onUnrenewable={onUnrenewable}
      />
    );
  }

  return (
    <LoginGrantConfirm
      grant={grant}
      lookup={shownLookup(phase.lookup, typed)}
      surface={surface}
      onCorrect={onCorrect}
      onInvalid={(stage) =>
        setPhase({ kind: "ended", lookup: phase.lookup, stage })
      }
    />
  );
}
